import {collection, doc, onSnapshot, orderBy, query} from 'firebase/firestore';
import {db} from '../Security/firebase-config';
import {eventSlots, parseSetupKey, resolveSetupEvent} from './eventSetup';
import {eventPrepIdentity, loadEventPrepFocusPoints} from './eventPrepFocusPoints';

const same = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const signature = points => JSON.stringify(points, (key, value) => key === 'createdAt' ? undefined : value);
const samePoint = (left, right) =>
  (left.sourceId && left.sourceId === right.sourceId && left.sourceType === right.sourceType && (left.sourceScope || null) === (right.sourceScope || null) && (left.sourceMapId || null) === (right.sourceMapId || null))
  || (left.text === right.text && (left.scope || 'general') === (right.scope || 'general') && (left.side || 'ATK & DEF') === (right.side || 'ATK & DEF') && same(left.assigneeUids || [], right.assigneeUids || []));

// Merge remote changes into an open editor without restoring locally removed
// points or overwriting fields the user has edited since the last snapshot.
export function mergeLiveFocusPointDraft(current, previous, next) {
  if (previous === null) {
    const used = new Set();
    const result = next.map(point => {
      const local = current.find(item => !used.has(item.id) && (item.id === point.id || samePoint(item, point)));
      if (!local) return point;
      used.add(local.id);
      return {...point, ...local, id: point.id};
    });
    return [...result, ...current.filter(point => !used.has(point.id))];
  }
  const before = new Map((previous || []).map(point => [point.id, point]));
  const local = new Map(current.map(point => [point.id, point]));
  const remote = new Map(next.map(point => [point.id, point]));
  const result = next.flatMap(point => {
    const old = before.get(point.id), edited = local.get(point.id);
    if (old && !edited) return []; // Local deletion wins.
    if (!edited) return [point];
    if (!old) return [edited]; // Restored draft or a local addition.
    const changes = Object.fromEntries(Object.entries(edited).filter(([key, value]) => key !== 'createdAt' && !same(value, old[key])));
    return [{...point, ...changes}];
  });
  for (const point of current) {
    if (remote.has(point.id)) continue;
    const old = before.get(point.id);
    if (!old || signature([old]) !== signature([point])) result.push(point);
  }
  const oldOrder = (previous || []).filter(point => local.has(point.id)).map(point => point.id);
  const localOrder = current.filter(point => before.has(point.id)).map(point => point.id);
  if (!same(oldOrder, localOrder)) {
    const rank = new Map(current.map((point, index) => [point.id, index]));
    result.sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity));
  }
  return result;
}

// Shared match rows keep their saved ratings and content. Newly prepared
// priorities can appear alongside them in the open draft, without replacing
// rated rows or making a second copy of the same source point.
export function subscribeLiveEventFocusPoints({teamId, eventId = '', event = {}, mapId = '', matchId = '', onChange, onError}) {
  const identity = eventPrepIdentity(event, eventId);
  if (!teamId || identity.scope !== 'team' || (!identity.key && !matchId)) {
    onChange([]);
    return () => {};
  }
  let alive = true, version = 0, queued = false, prepReady = !identity.key, matchReady = true;
  let latestEvent = event, prepared = [], saved = [], lastSignature = null;
  let stopMatch = () => {}, matchPath = '', stopLegacy = () => {}, legacyKey = '';
  const stops = [];
  function emit() {
    if (!alive || !prepReady || !matchReady) return;
    const points = [...saved, ...prepared.filter(point => !saved.some(existing => samePoint(existing, point)))];
    const nextSignature = signature(points);
    if (nextSignature === lastSignature) return;
    lastSignature = nextSignature;
    onChange(points);
  }
  function fail(error) {
    if (alive) onError?.(error);
  }
  function reload() {
    version++;
    if (queued || !alive) return;
    queued = true;
    Promise.resolve().then(async () => {
      queued = false;
      if (!alive) return;
      const current = version;
      try {
        const points = identity.key ? await loadEventPrepFocusPoints({teamId, event: latestEvent, eventId}) : [];
        if (!alive || current !== version) return;
        prepared = points; prepReady = true; emit();
      } catch (error) { if (alive && current === version) fail(error); }
    });
  }
  function followMatch() {
    const setup = parseSetupKey(eventId || latestEvent.eventId);
    const slot = setup ? eventSlots(latestEvent).find(item => item.id === setup.slotId) : latestEvent;
    const targetMap = slot?.mapId || latestEvent.mapId || mapId;
    const targetMatch = slot?.matchId || latestEvent.matchId || matchId;
    const path = targetMap && targetMatch ? `teams/${teamId}/maps/${targetMap}/matches/${targetMatch}/focusPoints` : '';
    if (path === matchPath) return;
    stopMatch(); matchPath = path; saved = []; matchReady = !path;
    if (!path) {emit(); return;}
    stopMatch = onSnapshot(query(collection(db, ...path.split('/')), orderBy('order', 'asc')), snapshot => {
      if (!alive || matchPath !== path) return;
      saved = snapshot.docs.map(item => ({...item.data(), id: item.id}));
      matchReady = true; emit();
    }, fail);
  }
  function followLegacy() {
    const key = latestEvent.sessionPlanId || identity.key;
    if (!key || key === legacyKey) return;
    stopLegacy(); legacyKey = key;
    stopLegacy = onSnapshot(doc(db, 'teams', teamId, 'sessionPlans', key), reload, error => {
      if (error.code === 'permission-denied') reload(); else fail(error);
    });
  }
  followMatch();
  if (identity.key) {
    followLegacy();
    stops.push(onSnapshot(doc(db, 'teams', teamId, 'eventPreps', identity.key), reload, error => {
      if (error.code === 'permission-denied') reload(); else fail(error);
    }));
    stops.push(onSnapshot(doc(db, 'teams', teamId), snapshot => {
      if (!alive) return;
      const source = resolveSetupEvent(snapshot.data()?.events, eventId || event.eventId || identity.key);
      if (source) latestEvent = source;
      followMatch(); followLegacy(); reload();
    }, fail));
    reload();
  } else emit();
  return () => {alive = false; version++; stops.forEach(stop => stop()); stopMatch(); stopLegacy();};
}
